/** The `then` / `do` operation list (design §6.13): run in order, each an
    existing engine call — set a value, re-run an item change, add / complete /
    delete / match a line, look up, fetch or recalculate lines, run an action,
    validate, read a URL, jump, say something. */

import { ArrowDown, ArrowUp } from 'lucide-react'
import ExprInput from './ExprInput'
import TypedValueInput from './TypedValueInput'
import {
  ActionPicker, KeyValueEditor, LineFormPicker, RouteStringPicker, StepPicker, TargetPicker,
} from './pickers'
import { formByNo, lineFormFor, useWizardDesign } from './context'
import type { TokenGroup } from './context'
import type { WizardOp, WizardOpKind } from './types'
import { OP_KINDS, OP_LABEL, blankOp } from './types'
import {
  AddBtn, Check, Field, Grid2, RemoveBtn, SelectInput, StringList, TextInput,
  cardStyle, emptyHintStyle, iconBtn,
} from './ui'

function OpFields({ op, onChange, formNo, extraTokens }: {
  op: WizardOp
  onChange: (op: WizardOp) => void
  formNo?: string
  extraTokens?: TokenGroup[]
}) {
  const ctx = useWizardDesign()
  const patch = (p: Partial<WizardOp>) => onChange({ ...op, ...p })
  const lineForm = formByNo(ctx.base, op.form_no) || lineFormFor(ctx.base, ctx.model, formNo)
  const lineCols = (lineForm?.columns || []).map((c) => c.name)
  switch (op.op) {
    case 'set': {
      const useExpr = typeof op.expr === 'string'
      return (
        <>
          <Field label="Target">
            <TargetPicker value={op.target} onChange={(target) => patch({ target })} formNo={formNo} />
          </Field>
          <div style={{ display: 'flex', gap: 14, fontSize: 13, marginBottom: 6 }}>
            <label><input type="radio" checked={!useExpr}
              onChange={() => { const { expr: _e, ...rest } = op; onChange({ ...rest, value: '' }) }} /> Value (template)</label>
            <label><input type="radio" checked={useExpr}
              onChange={() => { const { value: _v, ...rest } = op; onChange({ ...rest, expr: '' }) }} /> Calculated (expression)</label>
          </div>
          {useExpr ? (
            <ExprInput value={op.expr} onChange={(expr) => patch({ expr })} formNo={formNo} extraTokens={extraTokens}
              placeholder="countif({lines.alloc_qty} < {lines.pick_qty}) > 0" />
          ) : (
            <TypedValueInput value={op.value} onChange={(value) => patch({ value })} formNo={formNo}
              extraTokens={extraTokens} placeholder="{line.found_qty}" />
          )}
          <div style={{ marginTop: 6 }}>
            <Check checked={op.item_change !== false} onChange={(v) => patch({ item_change: v ? undefined : false })}
              label="Run the column's item change" />
          </div>
        </>
      )
    }
    case 'item_change':
      return (
        <Field label="Column whose item change re-runs">
          <TargetPicker value={op.field} onChange={(field) => patch({ field })} formNo={formNo}
            allowVars={false} writable={false} />
        </Field>
      )
    case 'add_line':
      return (
        <>
          <Field label="Line form"><LineFormPicker value={op.form_no} onChange={(form_no) => patch({ form_no: form_no || undefined })} /></Field>
          <Field label="Set on the new line (column → template)">
            <KeyValueEditor value={op.set} onChange={(set) => patch({ set })} keyOptions={lineCols}
              formNo={op.form_no || formNo} extraTokens={extraTokens} keyPlaceholder="column" />
          </Field>
          <Check checked={op.complete !== false} onChange={(v) => patch({ complete: v })}
            label="Complete the line after setting it (row complete)" />
        </>
      )
    case 'complete_line':
    case 'delete_line':
      return (
        <Field label="Line form (default: the step's)">
          <LineFormPicker value={op.form_no} onChange={(form_no) => patch({ form_no: form_no || undefined })} />
        </Field>
      )
    case 'match_line':
      return (
        <>
          <Field label="Match on (line columns equal to the scanned values)">
            <StringList values={op.on} onChange={(on) => patch({ on })} suggestions={lineCols} />
          </Field>
          <Grid2>
            <Field label="Increment column">
              <SelectInput value={op.increment?.column?.replace(/^line\./, '')} empty="— none —"
                options={lineCols}
                onChange={(c) => patch({ increment: c ? { column: `line.${c}`, by: op.increment?.by ?? '{scan.30|1}' } : undefined })} />
            </Field>
            {op.increment && (
              <Field label="By">
                <ExprInput mode="template" value={op.increment.by} formNo={formNo} extraTokens={extraTokens}
                  onChange={(by) => patch({ increment: { column: op.increment!.column, by } })} />
              </Field>
            )}
          </Grid2>
          <Grid2>
            <Field label="When no line matches">
              <RouteStringPicker value={op.on_none} onChange={(on_none) => patch({ on_none })}
                options={[{ value: 'add_line', label: 'add a line' }]} />
            </Field>
            <Field label="When several lines match">
              <RouteStringPicker value={op.on_many} onChange={(on_many) => patch({ on_many })}
                options={[{ value: 'first', label: 'take the first' }, { value: 'pick', label: 'let the user pick' }]} />
            </Field>
          </Grid2>
        </>
      )
    case 'lookup':
      return (
        <>
          <Field label="Lookup — TABLE(DISPLAY, DATA)">
            <TextInput value={op.lookup} mono onChange={(lookup) => patch({ lookup })} placeholder="item_master(item_name, item_code)" />
          </Field>
          <Field label="Filter (wizard tokens allowed)">
            <ExprInput mode="template" value={op.filter} onChange={(filter) => patch({ filter })} formNo={formNo}
              extraTokens={extraTokens} placeholder="item_code = '{line.item_code}'" />
          </Field>
          <Field label="Set from the found row (target → its column)">
            <KeyValueEditor value={op.map} onChange={(map) => patch({ map: map as Record<string, string> })}
              valueMode="text" valuePlaceholder="COLUMN"
              keyRender={(k, setK) => <TargetPicker value={k} onChange={setK} formNo={formNo} />} />
          </Field>
        </>
      )
    case 'fetch_lines':
    case 'recalc_lines': {
      const sources = ctx.base?.line_sources?.[op.form_no || formNo || ctx.model.line_form || '2'] || []
      return (
        <Grid2>
          <Field label="Line form"><LineFormPicker value={op.form_no} onChange={(form_no) => patch({ form_no: form_no || undefined })} /></Field>
          <Field label="Line Entry source">
            {sources.length
              ? <SelectInput value={op.source} onChange={(source) => patch({ source })} options={sources} empty="— default —" />
              : <TextInput value={op.source} onChange={(source) => patch({ source })} placeholder="source name" />}
          </Field>
          {op.op === 'fetch_lines' && (
            <Field label="Rows">
              <SelectInput value={op.select || 'all'} onChange={(select) => patch({ select: select as 'all' | 'ask' })}
                options={[{ value: 'all', label: 'Take all' }, { value: 'ask', label: 'Let the user pick' }]} />
            </Field>
          )}
        </Grid2>
      )
    }
    case 'run_action':
      return <Field label="Action"><ActionPicker value={op.action} onChange={(action) => patch({ action })} /></Field>
    case 'validate':
      return (
        <Field label="Scope">
          <SelectInput value={op.scope || 'form'} onChange={(scope) => patch({ scope: scope as 'form' | 'tree' })}
            options={[{ value: 'form', label: 'This form' }, { value: 'tree', label: 'The whole record (dry run)' }]} />
        </Field>
      )
    case 'fetch_url':
      return (
        <Field label="Use the request of step (default: this step's own)">
          <StepPicker value={op.step} onChange={(step) => patch({ step: step || undefined })} empty="— this step —" />
        </Field>
      )
    case 'goto':
      return <Field label="Go to"><StepPicker value={op.goto} onChange={(goto) => patch({ goto })} /></Field>
    case 'message':
      return (
        <Grid2>
          <Field label="Message">
            <ExprInput mode="template" value={op.message} onChange={(message) => patch({ message })} formNo={formNo} extraTokens={extraTokens} />
          </Field>
          <Field label="Kind">
            <SelectInput value={op.kind || 'info'} onChange={(kind) => patch({ kind: kind as WizardOp['kind'] })}
              options={['info', 'success', 'warning', 'error']} />
          </Field>
        </Grid2>
      )
    default:
      return null
  }
}

export default function OpsEditor({
  ops, onChange, formNo, extraTokens, emptyText = 'No operations.',
}: {
  ops: WizardOp[] | undefined
  onChange: (ops: WizardOp[]) => void
  formNo?: string
  extraTokens?: TokenGroup[]
  emptyText?: string
}) {
  const ctx = useWizardDesign()
  const list = ops || []
  const set = (i: number, op: WizardOp) => onChange(list.map((o, j) => (j === i ? op : o)))
  const move = (i: number, d: number) => {
    const j = i + d
    if (j < 0 || j >= list.length) return
    const next = [...list]
    ;[next[i], next[j]] = [next[j], next[i]]
    onChange(next)
  }
  return (
    <div>
      {list.length === 0 && <div style={emptyHintStyle}>{emptyText}</div>}
      {list.map((op, i) => (
        <div key={i} style={{ ...cardStyle, padding: 8, marginBottom: 6 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', width: 16 }}>{i + 1}.</span>
            <div style={{ flex: 1 }}>
              <SelectInput value={op.op} disabled={ctx.readOnly}
                onChange={(k) => set(i, blankOp(k as WizardOpKind))}
                options={OP_KINDS.map((k) => ({ value: k, label: OP_LABEL[k] }))} />
            </div>
            {!ctx.readOnly && (
              <>
                <button type="button" style={iconBtn} title="Move up" onClick={() => move(i, -1)}><ArrowUp size={13} /></button>
                <button type="button" style={iconBtn} title="Move down" onClick={() => move(i, 1)}><ArrowDown size={13} /></button>
                <RemoveBtn onClick={() => onChange(list.filter((_, j) => j !== i))} />
              </>
            )}
          </div>
          <OpFields op={op} onChange={(o) => set(i, o)} formNo={formNo} extraTokens={extraTokens} />
        </div>
      ))}
      {!ctx.readOnly && <AddBtn onClick={() => onChange([...list, blankOp('set')])}>Add operation</AddBtn>}
    </div>
  )
}
